import clsx from 'clsx'
import React, { useMemo, useState } from 'react'
import { ZinRadio } from '@tnl/zin-react'
import { VIPBadge } from '@components/ui/VIPBadge'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { IMkAuthorizedSession } from '@auth/types'
import type { ContractDetails } from '@shared/types/membership'
import './MembershipSwitcherDropdown.scss'
import Card from '../Card/Card'
import { Divider, DividerOrientation, DividerSpacing } from '../Divider'

export interface MembershipSwitcherDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string
  selectOwnerIdLabel: string
  contractDetails?: ContractDetails[]
  badgeClassName?: string
  invert?: boolean
}

export const MembershipSwitcherDropdown: React.FC<MembershipSwitcherDropdownProps> = ({
  className,
  label,
  selectOwnerIdLabel,
  contractDetails,
  badgeClassName,
  invert = false,
}) => {
  const { isAuthorized, session, switchMembership } = useAuthentication()
  const [isOpen, setIsOpen] = useState(false)
  const [isSwitchingMembership, setIsSwitchingMembership] = useState(false)
  const hasContracts = (contractDetails?.length ?? 0) > 0
  const hasMultipleMemberships = (contractDetails?.length ?? 0) > 1
  const selectedMemberNumber = isAuthorized ? ((session as IMkAuthorizedSession | undefined)?.authorizedUser?.memberNumber ?? null) : null

  const selectedContract = useMemo(
    () => contractDetails?.find((contract) => contract.memberNumber === selectedMemberNumber) ?? contractDetails?.[0],
    [contractDetails, selectedMemberNumber],
  )

  const handleMembershipSelect = async (contract: ContractDetails) => {
    if (isSwitchingMembership || contract.memberNumber === selectedMemberNumber) {
      return
    }

    setIsSwitchingMembership(true)
    try {
      await switchMembership(contract.memberNumber)
    } finally {
      setIsSwitchingMembership(false)
    }
  }

  return (
    <div className={clsx('membership-switcher', { 'membership-switcher--inverted': invert }, className)}>
      <VIPBadge
        className={badgeClassName}
        label={selectedContract?.tier ?? label}
        onToggle={() => setIsOpen((prev) => !prev)}
        isOpen={isOpen}
        isInteractive={hasContracts}
      />
      {hasContracts && isOpen && (
        <Card
          className="membership-switcher__container"
          inversed={invert}>
          {hasMultipleMemberships && <div className="membership-switcher__subtitle subtitle-3">{selectOwnerIdLabel}</div>}
          {contractDetails?.map((contract) => {
            const isSelected = contract.memberNumber === selectedContract?.memberNumber

            return (
              <div
                key={contract.memberNumber}
                className={clsx('membership-switcher__contract-details', {
                  'membership-switcher__contract-details--selected': isSelected,
                  'membership-switcher__contract-details--single': !hasMultipleMemberships,
                })}>
                <label
                  className={clsx('membership-switcher__contract-details__content', {
                    'membership-switcher__contract-details__content--clickable': hasMultipleMemberships,
                  })}
                  onClick={hasMultipleMemberships ? () => void handleMembershipSelect(contract) : undefined}>
                  <span className="membership-switcher__contract-details__item body-3">#{contract.memberNumber}</span>
                  <Divider
                    orientation={DividerOrientation.Inline}
                    spacing={DividerSpacing.Lg}
                    decorative
                    inversed={invert}
                  />
                  <span className="membership-switcher__contract-details__item body-3">{contract.contractType}</span>
                  <Divider
                    orientation={DividerOrientation.Inline}
                    spacing={DividerSpacing.Lg}
                    decorative
                    inversed={invert}
                  />
                  <span className="membership-switcher__contract-details__item body-3">
                    {contract.points} {contract.currency}
                  </span>
                  <Divider
                    orientation={DividerOrientation.Inline}
                    spacing={DividerSpacing.Lg}
                    decorative
                    inversed={invert}
                  />
                  <span className="membership-switcher__contract-details__item body-3">{contract.tier}</span>
                </label>
                {hasMultipleMemberships && (
                  <ZinRadio
                    className="membership-switcher__contract-details__radio"
                    invert={!invert}
                    checked={isSelected}
                    disabled={isSwitchingMembership}
                    onInputChange={() => void handleMembershipSelect(contract)}
                  />
                )}
              </div>
            )
          })}
        </Card>
      )}
    </div>
  )
}
